import { useRef } from "react";

import { cn, customTv } from "#utils";
import { createCalendar } from "@internationalized/date";
import { useDateField, useDateSegment, useLocale } from "react-aria";
import {
  type DateFieldProps as AriaDateFieldProps,
  type DateSegmentProps as AriaDateSegmentProps,
  type DateValue,
  Input,
} from "react-aria-components";
import {
  type DateFieldState,
  type DateSegment as AriaDateSegment,
  useDateFieldState,
} from "react-stately";

export function TimeField({ ...props }: AriaDateFieldProps<DateValue>) {
  const { locale } = useLocale();
  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  });

  const ref = useRef(null);
  const { fieldProps } = useDateField(props, state, ref);

  const hourSegment = state.segments.find((segment) => segment.type === "hour");
  const minuteSegment = state.segments.find((segment) => segment.type === "minute");

  return (
    <div
      {...fieldProps}
      className={cn("flex items-center gap-0", state.isInvalid ? "!text-text-negative" : "")}
      ref={ref}
    >
      {hourSegment && minuteSegment ? (
        <>
          <DateSegment
            aria-label={`date-segment-${hourSegment.text}`}
            segment={hourSegment}
            state={state}
          />
          <span className="px-xs font-bold">:</span>
          <DateSegment
            aria-label={`date-segment-${minuteSegment.text}`}
            segment={minuteSegment}
            state={state}
          />
        </>
      ) : null}
    </div>
  );
}

interface DateSegmentProps extends AriaDateSegmentProps {
  segment: AriaDateSegment;
  state: DateFieldState;
  className?: string;
}

const timeSegmentInputStyles = customTv({
  base: `
    size-full h-8 w-8 rounded-1 border border-outline-input-enabled bg-transparent py-xs
    text-center text-label-small font-semibold text-text-secondary !caret-text-primary outline-none
    hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover
    focus:border-outline-input-focus focus:ring-1 focus:ring-outline-input-focus
  `,
});

function DateSegment({ segment, state, className }: DateSegmentProps) {
  const ref = useRef(null);
  const { segmentProps } = useDateSegment(segment, state, ref);
  return (
    <Input
      {...segmentProps}
      aria-label={`date-segment-${segment.text}`}
      className={timeSegmentInputStyles({ class: className })}
      onChange={(e) => {
        state.setSegment(segment.type, Number(e.target.value));
      }}
      onFocus={(e) => {
        e.target.select();
      }}
      value={segment.text}
    />
  );
}
